/**
 * @file recently-viewed-products.js
 *
 * Two responsibilities:
 *  1. RecentlyViewed – utility used by predictive-search to track product IDs
 *     (unchanged from original, exported for backward-compatibility).
 *  2. RecentlyViewedProducts – custom element that reads bma_rv_handles,
 *     fetches live product data and renders a fixed responsive grid.
 */

// ─── Utility: track by product ID (used by predictive-search) ─────────────

export class RecentlyViewed {
  /** @static @constant {string} */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} */
  static #MAX_PRODUCTS = 4;

  /**
   * @param {string} productId
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();
    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);
    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  /**
   * Track a product handle in bma_rv_handles.
   * Called from scripts.liquid on every PDP visit.
   * @param {string} handle
   */
  static addHandle(handle) {
    if (!handle) return;
    const KEY = 'bma_rv_handles';
    const MAX = 20;
    try {
      let handles = /** @type {string[]} */ (JSON.parse(localStorage.getItem(KEY) || '[]'));
      if (!Array.isArray(handles)) handles = [];
      handles = handles.filter((/** @type {string} */ h) => h !== handle);
      handles.unshift(handle);
      localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX)));
    } catch {
      /* ignore storage errors (private mode, quota) */
    }
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /** @returns {string[]} */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Build the grid HTML.
 * @param {string[]} cards
 */
function buildGrid(cards) {
  const items = cards.map((c) => `<div class="resource-list__item">${c}</div>`).join('');
  return (
    `<div class="resource-list resource-list--grid" ` +
    `style="--resource-list-columns: repeat(4, 1fr); --resource-list-columns-mobile: repeat(2, 1fr);">` +
    items +
    `</div>`
  );
}

// ─── Custom element ────────────────────────────────────────────────────────

class RecentlyViewedProducts extends HTMLElement {
  static #STORAGE_KEY = 'bma_rv_handles';
  static #MAX_STORE = 20;

  /** @type {boolean} */
  #clearClickBound = false;

  connectedCallback() {
    const currentHandle = this.dataset.currentHandle || '';

    // Track current PDP handle (also done via scripts.liquid, but belt-and-suspenders)
    if (currentHandle) {
      this.#trackHandle(currentHandle);
    }

    if (!this.#clearClickBound) {
      this.#clearClickBound = true;
      this.addEventListener('click', (e) => {
        if (e.target instanceof Element && e.target.closest('[data-rv-clear]')) {
          this.#clearHistory();
        }
      });
    }

    this.#load(currentHandle);
  }

  #clearHistory() {
    try {
      localStorage.removeItem(RecentlyViewedProducts.#STORAGE_KEY);
      RecentlyViewed.clearProducts();
    } catch {
      /* ignore */
    }
    delete this.dataset.ready;
    const grid = this.querySelector('[data-rv-grid]');
    if (grid) grid.innerHTML = '';
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /** @returns {string[]} */
  #getHandles() {
    try {
      const raw = localStorage.getItem(RecentlyViewedProducts.#STORAGE_KEY);
      const parsed = JSON.parse(raw || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      console.warn('[recently-viewed] corrupt localStorage value – resetting');
      return [];
    }
  }

  /** @param {string} handle */
  #trackHandle(handle) {
    try {
      let handles = this.#getHandles();
      handles = handles.filter((h) => h !== handle);
      handles.unshift(handle);
      localStorage.setItem(
        RecentlyViewedProducts.#STORAGE_KEY,
        JSON.stringify(handles.slice(0, RecentlyViewedProducts.#MAX_STORE))
      );
    } catch {
      /* ignore quota / private-mode errors */
    }
  }

  /** @param {string} currentHandle */
  async #load(currentHandle) {
    const maxProducts = parseInt(this.dataset.productsToShow || '4', 10);

    // Exclude the current PDP product; limit to maxProducts
    const handles = this.#getHandles()
      .filter((h) => h !== currentHandle)
      .slice(0, maxProducts);

    if (!handles.length) return; // nothing to show → section stays hidden

    // Fetch all cards in parallel via Section Rendering API.
    // /products/{handle}?sections=rv-product-card returns Liquid-rendered card HTML
    // (same snippet as predictive-search: brand logo, title, price, availability badge).
    // A 404 response means the product was deleted → skip it silently.
    const fetches = handles.map((handle) =>
      fetch(`/products/${handle}?sections=rv-product-card`)
        .then((r) => (r.ok ? r.json() : null))
        .then((/** @type {any} */ j) => (j ? /** @type {string} */ (j['rv-product-card']) : null))
        .catch(() => null)
    );

    const results = await Promise.allSettled(fetches);

    const cards = /** @type {string[]} */ (results
      .filter((r) => r.status === 'fulfilled' && /** @type {PromiseFulfilledResult<any>} */ (r).value)
      .map((r) => /** @type {PromiseFulfilledResult<any>} */ (r).value));

    if (!cards.length) return; // all handles were 404 or errored

    this.#render(cards);
  }

  /** @param {string[]} cards  Server-rendered card HTML strings */
  #render(cards) {
    const grid = this.querySelector('[data-rv-grid]');
    if (!grid) return;

    grid.innerHTML = buildGrid(cards);

    // Show section – triggers CSS: recently-viewed-products[data-ready] { display: revert }
    this.dataset.ready = '';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
